import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type FC,
} from "react";
import { LeftOutlined } from "@ant-design/icons";
import { message } from "antd";
import { useNavigate, useParams } from "react-router-dom";

import AppContext from "../../../context/AppContext";
import { AppStrings } from "../../../utils/constants/AppStrings";
import { SIDER_TABS } from "../../../utils/Constants";
import { Button } from "../../shared";
import { useStaffRegulator } from "../../../queries/staff-regulators/useStaffRegulator";
import { useApproveStaffRegulator } from "../../../queries/staff-regulators/useApproveStaffRegulator";
import { useRevokeStaffRegulator } from "../../../queries/staff-regulators/useRevokeStaffRegulator";
import { staffRegulatorsErrorMessage } from "../staffRegulatorsErrorMessage";
import { STAFF_REGULATORS_LIST_ROUTE } from "../staffRegulatorsRoutes";
import StaffRegulatorStatusBadge from "../StaffRegulatorStatusBadge/StaffRegulatorStatusBadge";
import { staffRegulatorDisplayName } from "../staffRegulatorsData";
import { usePageTitle } from "../../CommonComponents/Layout/PageTitleContext";

import styles from "./StaffRegulatorProfile.module.css";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

const StaffRegulatorProfile: FC = () => {
  const navigate = useNavigate();
  const { regulatorId } = useParams<{ regulatorId?: string }>();
  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;
  const decodedId = useMemo(() => {
    if (!regulatorId) return "";
    try {
      return decodeURIComponent(regulatorId);
    } catch {
      return regulatorId;
    }
  }, [regulatorId]);

  const detailQuery = useStaffRegulator(decodedId || undefined);
  const approve = useApproveStaffRegulator();
  const revoke = useRevokeStaffRegulator();

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.COMPLIANCE, "COMPLIANCE_REGULATORS");
  }, [setActiveSidebarTab]);

  const handleBack = useCallback(() => {
    navigate(STAFF_REGULATORS_LIST_ROUTE);
  }, [navigate]);

  const handleApprove = useCallback(async () => {
    try {
      await approve.mutateAsync(decodedId);
      message.success(AppStrings.COMPLIANCE_REGULATOR_APPROVE_SUCCESS);
    } catch (error) {
      message.error(
        staffRegulatorsErrorMessage(
          error,
          AppStrings.COMPLIANCE_REGULATOR_ACTION_ERROR,
        ),
      );
    }
  }, [approve, decodedId]);

  const handleRevoke = useCallback(async () => {
    try {
      await revoke.mutateAsync(decodedId);
      message.success(AppStrings.COMPLIANCE_REGULATOR_REVOKE_SUCCESS);
      navigate(STAFF_REGULATORS_LIST_ROUTE);
    } catch (error) {
      message.error(
        staffRegulatorsErrorMessage(
          error,
          AppStrings.COMPLIANCE_REGULATOR_ACTION_ERROR,
        ),
      );
    }
  }, [decodedId, navigate, revoke]);

  const regulator = detailQuery.data;
  usePageTitle(
    regulator
      ? staffRegulatorDisplayName(regulator)
      : AppStrings.COMPLIANCE_REGULATOR_PROFILE,
  );
  const loadError =
    detailQuery.isError && !regulator
      ? staffRegulatorsErrorMessage(
          detailQuery.error,
          AppStrings.COMPLIANCE_REGULATOR_LOAD_ERROR,
        )
      : null;

  return (
    <>
      <div className={styles.page} data-testid="staff-regulator-profile">
        <div className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            aria-label={AppStrings.COMPLIANCE_REGULATOR_BACK}
            onClick={handleBack}
          >
            <LeftOutlined className={styles.backButtonIcon} />
          </button>
          {regulator?.status === "pending" ? (
            <Button
              size="large"
              onClick={handleApprove}
              loading={approve.isPending}
              data-testid="staff-regulator-approve"
            >
              {AppStrings.COMPLIANCE_REGULATOR_APPROVE}
            </Button>
          ) : null}
          {regulator?.status === "approved" ? (
            <Button
              size="large"
              intent="danger"
              onClick={handleRevoke}
              loading={revoke.isPending}
              data-testid="staff-regulator-revoke"
            >
              {AppStrings.COMPLIANCE_REGULATOR_REVOKE}
            </Button>
          ) : null}
        </div>

        {detailQuery.isLoading ? (
          <p>{AppStrings.COMPLIANCE_REGULATOR_LOADING}</p>
        ) : null}
        {loadError ? <p role="alert">{loadError}</p> : null}
        {regulator ? (
          <dl className={styles.details}>
            <div>
              <dt>{AppStrings.ID}</dt>
              <dd>{regulator.admin_id}</dd>
            </div>
            <div>
              <dt>{AppStrings.EMAIL}</dt>
              <dd>{regulator.email}</dd>
            </div>
            <div>
              <dt>{AppStrings.STATUS}</dt>
              <dd>
                <StaffRegulatorStatusBadge status={regulator.status} />
              </dd>
            </div>
            <div>
              <dt>{AppStrings.COMPLIANCE_REGULATOR_TIMEZONE}</dt>
              <dd>{regulator.timezone ?? AppStrings.EMPTY_REASON}</dd>
            </div>
            <div>
              <dt>{AppStrings.COMPLIANCE_REGULATOR_WHITELISTED_IPS}</dt>
              <dd>
                {regulator.whitelisted_ips.length
                  ? regulator.whitelisted_ips.join(", ")
                  : AppStrings.EMPTY_REASON}
              </dd>
            </div>
          </dl>
        ) : null}
      </div>
    </>
  );
};

export default StaffRegulatorProfile;
